import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Tags } from "lucide-react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { ListEmpty, PageHeader } from "@/components/patterns";
import { dateLocale } from "@/lib/dateTime";

export interface PriceListRow {
  id: string;
  name: string;
  description?: string | null;
  adjust_pct: number | string;
  valid_from?: string | null;
  valid_to?: string | null;
  status: boolean;
  usable_today: boolean;
  item_count: number;
  account_count: number;
}

const th = "h-[34px] border-b border-border px-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const td = "border-b border-border/60 px-2.5 py-2";
const formatDate = (d?: string | null) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "");

export const adjustText = (n: number | string) => {
  const v = Number(n) || 0;
  return v === 0 ? "Catalog price" : v < 0 ? `${Math.abs(v)}% below catalog` : `${v}% above catalog`;
};

/** The sets of prices customers can be given */
export function PriceListsPage() {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [lists, setLists] = useState<PriceListRow[] | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", adjust_pct: "0", description: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLists(unwrapArray<PriceListRow>(await api.get("/api/PriceList/List?all=true")));
    } catch (e: any) {
      error("Could not load", e?.message || "Please try again");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res: any = await api.post("/api/PriceList/Insert", { name: form.name.trim(), adjust_pct: Number(form.adjust_pct) || 0, description: form.description.trim() || undefined });
      const created = res?.AddtionalData ?? res?.data ?? res;
      success("Price list created", form.name.trim());
      setOpen(false);
      navigate(`/price-lists/${created.id}`);
    } catch (err: any) {
      error("Could not create", err?.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Price lists"
        summary={lists?.length}
        actions={
          <Button size="sm" onClick={() => (setForm({ name: "", adjust_pct: "0", description: "" }), setOpen(true))}>
            <Plus className="size-4 mr-1.5" /> New price list
          </Button>
        }
      />
      <p className="text-[13px] text-muted-foreground">
        A price list is what a kind of customer pays: set a price per product (with quantity slabs), or move the whole catalog up or down. Give a list to a customer on their account; their quotes use it.
      </p>

      {!lists ? (
        <TableSkeleton rows={4} columns={5} />
      ) : lists.length === 0 ? (
        <ListEmpty noun="price lists" icon={Tags} filtered={false} firstUse="Make a list such as Dealer or Export, then give it to customers." createLabel="+ New price list" onCreate={() => setOpen(true)} onClear={() => {}} />
      ) : (
        <div className="overflow-x-auto rounded-[10px] border border-border bg-card">
          <table className="w-full min-w-[640px] border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-muted/40">
                <th className={th}>Name</th>
                <th className={th}>Other products</th>
                <th className={th}>Prices set</th>
                <th className={th}>Customers</th>
                <th className={th}>Valid</th>
                <th className={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((l) => (
                <tr key={l.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/price-lists/${l.id}`)}>
                  <td className={`${td} font-medium`}>{l.name}</td>
                  <td className={td}>{adjustText(l.adjust_pct)}</td>
                  <td className={td}>{l.item_count}</td>
                  <td className={td}>{l.account_count}</td>
                  <td className={td}>{l.valid_from || l.valid_to ? `${formatDate(l.valid_from) || "start"} → ${formatDate(l.valid_to) || "no end"}` : "Always"}</td>
                  <td className={td}>{!l.status ? <Badge variant="outline">Off</Badge> : l.usable_today ? <Badge variant="success">In use</Badge> : <Badge variant="warning">Outside its dates</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New price list</DialogTitle>
            <DialogDescription>You add the product prices on the next page.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} placeholder="e.g. Dealer, Export" required /></div>
            <div className="space-y-1.5">
              <Label>Products you don't price (%)</Label>
              <Input type="number" min={-90} max={500} step="0.5" value={form.adjust_pct} onChange={(e) => setForm({ ...form, adjust_pct: e.target.value })} />
              <p className="text-[11px] text-muted-foreground">-15 = 15% below the catalog price, 10 = 10% above, 0 = catalog price</p>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.name.trim()}>{busy && <Loader2 className="size-4 animate-spin mr-2" />}Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
